import { useState, useCallback, useEffect } from 'react';
import { Grid3X3, Plus, RefreshCw, CheckCircle, Filter, Upload } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────

interface SkillEntry {
  skill_id: string;
  skill_name: string;
  category: string;
  proficiency: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  verified: boolean;
}

interface CertEntry {
  name: string;
  status: string;
  expiry_date?: string;
}

interface MatrixRow {
  employee_id: string;
  employee_name: string;
  title: string;
  department: string;
  skills: SkillEntry[];
  certifications: CertEntry[];
  skill_count: number;
  cert_count: number;
}

interface MatrixData {
  matrix: MatrixRow[];
  skill_columns: string[];
  categories: string[];
  total_employees: number;
}

// ─── Proficiency Dot ─────────────────────────────────────────

function ProficiencyDot({ proficiency, verified }: { proficiency: string; verified: boolean }) {
  const colors: Record<string, string> = {
    expert: 'bg-amber-500',
    advanced: 'bg-green-500',
    intermediate: 'bg-blue-500',
    beginner: 'bg-muted-foreground',
  };
  return (
    <div className="flex items-center justify-center">
      <div className={`w-3 h-3 rounded-full ${colors[proficiency] ?? colors['beginner']} ${verified ? 'ring-1 ring-white/30' : 'opacity-70'}`} title={`${proficiency}${verified ? ' (verified)' : ''}`} />
    </div>
  );
}

// ─── Add Skill Modal ─────────────────────────────────────────

interface AddSkillModalProps {
  employeeId: string;
  employeeName: string;
  onClose: () => void;
  onAdded: () => void;
}

const SKILL_CATEGORIES = ['Technical', 'Networking', 'Security', 'Cloud', 'Scripting', 'Soft Skills', 'Management', 'Vendor', 'Other'];

function AddSkillModal({ employeeId, employeeName, onClose, onAdded }: AddSkillModalProps) {
  const [form, setForm] = useState({
    skill_name: '',
    category: 'Technical',
    proficiency: 'intermediate' as const,
    years_experience: 0,
    verified: false,
    source: 'manager_verified' as const,
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!form.skill_name.trim()) { setError('Skill name is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/people/skills/${employeeId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Failed to save skill');
      onAdded();
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="glass rounded-xl p-6 w-full max-w-md space-y-4">
        <h3 className="text-sm font-semibold text-foreground">Add Skill — {employeeName}</h3>
        {error && <p className="text-red-400 text-sm">{error}</p>}

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-xs text-muted-foreground block mb-1">Skill Name</label>
            <input
              value={form.skill_name}
              onChange={e => setForm(f => ({ ...f, skill_name: e.target.value }))}
              placeholder="e.g. Cisco IOS, PowerShell, Azure..."
              className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Category</label>
            <select
              value={form.category}
              onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
              className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
            >
              {SKILL_CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Proficiency</label>
            <select
              value={form.proficiency}
              onChange={e => setForm(f => ({ ...f, proficiency: e.target.value as typeof form.proficiency }))}
              className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
            >
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
              <option value="expert">Expert</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Years Experience</label>
            <input
              type="number"
              min="0"
              max="50"
              value={form.years_experience}
              onChange={e => setForm(f => ({ ...f, years_experience: parseInt(e.target.value) || 0 }))}
              className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
            />
          </div>
          <div className="flex items-center gap-2 pt-5">
            <input
              type="checkbox"
              id="verified"
              checked={form.verified}
              onChange={e => setForm(f => ({ ...f, verified: e.target.checked }))}
              className="rounded border-border"
            />
            <label htmlFor="verified" className="text-sm text-foreground">Manager verified</label>
          </div>
          <div className="col-span-2">
            <label className="text-xs text-muted-foreground block mb-1">Notes (optional)</label>
            <input
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              className="w-full bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
            />
          </div>
        </div>

        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:bg-surface-3">Cancel</button>
          <button onClick={() => void save()} disabled={saving} className="px-4 py-1.5 rounded-lg bg-amber-500 text-foreground hover:bg-amber-600 text-sm disabled:opacity-50">
            {saving ? 'Saving...' : 'Add Skill'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────

export function SkillsMatrixPage() {
  const [matrixData, setMatrixData] = useState<MatrixData | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{ message: string; success: boolean } | null>(null);
  const [addingFor, setAddingFor] = useState<{ id: string; name: string } | null>(null);
  const [filterDept, setFilterDept] = useState('');
  const [filterSkill, setFilterSkill] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterDept) params.set('department', filterDept);
      if (filterSkill) params.set('skill', filterSkill);
      const res = await fetch(`/api/people/skills-matrix?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json() as MatrixData;
      setMatrixData(data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [filterDept, filterSkill]);

  useEffect(() => { void load(); }, [load]);

  async function syncToPSA() {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await fetch('/api/people/skills/psa-sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json() as { message: string; success: boolean };
      setSyncResult(data);
    } catch (e) {
      setSyncResult({ message: (e as Error).message, success: false });
    } finally {
      setSyncing(false);
    }
  }

  const allDepts = [...new Set(matrixData?.matrix.map(r => r.department) ?? [])].sort();

  const proficiencyLegend = [
    { color: 'bg-amber-500', label: 'Expert' },
    { color: 'bg-green-500', label: 'Advanced' },
    { color: 'bg-blue-500', label: 'Intermediate' },
    { color: 'bg-muted-foreground', label: 'Beginner' },
  ];

  // Limit columns shown in grid view
  const visibleSkills = matrixData?.skill_columns.slice(0, 15) ?? [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Grid3X3 className="w-5 h-5 text-amber-500" />
          <h1 className="text-xl font-bold text-foreground">Skills Matrix</h1>
          {matrixData && <span className="text-sm text-muted-foreground">{matrixData.total_employees} employees</span>}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => void syncToPSA()}
            disabled={syncing}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-3 hover:bg-surface-2 text-sm text-foreground disabled:opacity-50"
          >
            <Upload className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync to PSA'}
          </button>
          <button
            onClick={() => void load()}
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-surface-3"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {syncResult && (
        <div className={`p-3 rounded-lg text-sm ${syncResult.success ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'}`}>
          {syncResult.message}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Filter className="w-3.5 h-3.5" />
          Filters:
        </div>
        <select
          value={filterDept}
          onChange={e => setFilterDept(e.target.value)}
          className="bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
        >
          <option value="">All Departments</option>
          {allDepts.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <input
          value={filterSkill}
          onChange={e => setFilterSkill(e.target.value)}
          placeholder="Filter by skill..."
          className="bg-surface-3 border border-border rounded-lg px-3 py-1.5 text-sm text-foreground w-48"
        />
        <div className="ml-auto flex items-center gap-1">
          <button
            onClick={() => setViewMode('grid')}
            className={`px-2.5 py-1 rounded text-xs ${viewMode === 'grid' ? 'bg-amber-500/20 text-amber-400' : 'text-muted-foreground hover:text-foreground'}`}
          >
            Grid
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`px-2.5 py-1 rounded text-xs ${viewMode === 'list' ? 'bg-amber-500/20 text-amber-400' : 'text-muted-foreground hover:text-foreground'}`}
          >
            List
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4">
        {proficiencyLegend.map(({ color, label }) => (
          <div key={label} className="flex items-center gap-1.5">
            <div className={`w-3 h-3 rounded-full ${color}`} />
            <span className="text-xs text-muted-foreground">{label}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5">
          <CheckCircle className="w-3 h-3 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">Manager verified</span>
        </div>
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm">Loading matrix...</div>
      ) : !matrixData || matrixData.matrix.length === 0 ? (
        <div className="glass rounded-xl p-12 text-center">
          <Grid3X3 className="w-12 h-12 text-amber-500/20 mx-auto mb-3" />
          <p className="text-foreground font-medium mb-1">No skills data yet</p>
          <p className="text-sm text-muted-foreground">Add skills to employees to build the matrix.</p>
        </div>
      ) : viewMode === 'grid' ? (
        /* Grid view — scrollable matrix */
        <div className="glass rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground bg-surface-2 sticky left-0 min-w-[180px]">Employee</th>
                  <th className="text-center px-2 py-3 font-medium text-muted-foreground bg-surface-2 min-w-[60px]">Skills</th>
                  <th className="text-center px-2 py-3 font-medium text-muted-foreground bg-surface-2 min-w-[60px]">Certs</th>
                  {visibleSkills.map(skill => (
                    <th key={skill} className="text-center px-2 py-3 font-medium text-muted-foreground min-w-[100px]">
                      <div className="truncate max-w-[90px] mx-auto" title={skill}>{skill}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matrixData.matrix.map(row => {
                  const skillMap = new Map(row.skills.map(s => [s.skill_name.toLowerCase(), s]));
                  return (
                    <tr key={row.employee_id} className="border-b border-border/50 hover:bg-surface-3/50">
                      <td className="px-4 py-3 sticky left-0 bg-[#09090b]">
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <p className="font-medium text-foreground">{row.employee_name}</p>
                            <p className="text-muted-foreground">{row.title}</p>
                          </div>
                          <button
                            onClick={() => setAddingFor({ id: row.employee_id, name: row.employee_name })}
                            className="text-amber-500 hover:text-amber-400 opacity-0 group-hover:opacity-100"
                          >
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                      <td className="text-center px-2 py-3 text-foreground">{row.skill_count}</td>
                      <td className="text-center px-2 py-3 text-foreground">{row.cert_count}</td>
                      {visibleSkills.map(skill => {
                        const entry = skillMap.get(skill.toLowerCase());
                        return (
                          <td key={skill} className="text-center px-2 py-3">
                            {entry ? <ProficiencyDot proficiency={entry.proficiency} verified={entry.verified} /> : (
                              <div className="flex items-center justify-center">
                                <div className="w-3 h-3 rounded-full border border-border/40" />
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {(matrixData.skill_columns.length ?? 0) > 15 && (
            <div className="px-4 py-2 text-xs text-muted-foreground border-t border-border">
              Showing first 15 of {matrixData.skill_columns.length} skills. Use the skill filter to narrow down.
            </div>
          )}
        </div>
      ) : (
        /* List view */
        <div className="space-y-3">
          {matrixData.matrix.map(row => (
            <div key={row.employee_id} className="glass rounded-xl p-4">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <p className="font-medium text-sm text-foreground">{row.employee_name}</p>
                  <p className="text-xs text-muted-foreground">{row.title} &middot; {row.department}</p>
                </div>
                <button
                  onClick={() => setAddingFor({ id: row.employee_id, name: row.employee_name })}
                  className="flex items-center gap-1 px-2.5 py-1 rounded bg-amber-500/10 text-amber-500 hover:bg-amber-500/20 text-xs"
                >
                  <Plus className="w-3 h-3" /> Add Skill
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {row.skills.map(skill => (
                  <span key={skill.skill_id} className={`px-2.5 py-1 rounded-full text-xs flex items-center gap-1 ${
                    skill.proficiency === 'expert' ? 'bg-amber-500/20 text-amber-400' :
                    skill.proficiency === 'advanced' ? 'bg-green-500/20 text-green-400' :
                    skill.proficiency === 'intermediate' ? 'bg-blue-500/20 text-blue-400' :
                    'bg-muted/20 text-muted-foreground'
                  }`}>
                    {skill.verified && <CheckCircle className="w-2.5 h-2.5" />}
                    {skill.skill_name}
                    <span className="opacity-70">· {skill.proficiency.slice(0, 3)}</span>
                  </span>
                ))}
                {row.certifications.map((cert, i) => (
                  <span key={i} className={`px-2.5 py-1 rounded-full text-xs bg-purple-500/20 text-purple-400 flex items-center gap-1 ${cert.status === 'expired' ? 'opacity-50' : ''}`}>
                    🏆 {cert.name}
                  </span>
                ))}
                {row.skills.length === 0 && row.certifications.length === 0 && (
                  <span className="text-xs text-muted-foreground">No skills recorded yet</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {addingFor && (
        <AddSkillModal
          employeeId={addingFor.id}
          employeeName={addingFor.name}
          onClose={() => setAddingFor(null)}
          onAdded={() => void load()}
        />
      )}
    </div>
  );
}
